import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  AsianRaceSubType,
  BankruptType,
  CounselingFormat,
  DemographicInformationProvisionType,
  EthnicityType,
  HispanicOrLatinoEthnicitySubType,
  HomeBuyerCounseling,
  NativeHawaiianOrOtherRaceSubType,
  RaceType,
} from 'constants/loanApplication';
import {
  asianRaceOptionsMap,
  hispanicOrLatinoEthnicityOptionsMap,
  nativeHawaiianOrOtherRaceOptionsMap,
} from 'constants/options';
import { SEX_NOT_PROVIDED_VALUE } from 'constants/profile';
import { getConditionalObject, replaceEmptyWithFalse, replaceEmptyWithNull } from 'utils/data';

import { DeclarationType } from './Declarations.constants';

const asianOptions = Object.values(AsianRaceSubType);
const hawaiianOptions = Object.values(NativeHawaiianOrOtherRaceSubType);

const clearRaceFields = () => {
  return {
    isAmericanIndianOrAlaskaNative: null,
    isAsian: null,
    isBlackOrAfricanAmerican: null,
    isNativeHawaiianOrOtherPacificIslander: null,
    isWhite: null,
    otherPacificIslander: null,
    otherAsianRace: null,
    americanTribeName: null,
    otherPacificIslanderOptions: null,
    otherAsianOptions: null,
  };
};

const fillOptionsWithFalse = options =>
  Object.values(options).reduce((acc, value) => {
    acc[value] = false;

    return acc;
  }, {});

export const normalizeReceivedDeclarations = declarations => {
  const formattedDeclarations = _.cloneDeep(declarations);

  const { demographicInformation, finances } = declarations;

  const {
    race,
    ethnicity,
    sex,
    demographicInformationProvisionType,
    isSexInfoNotProvidedDuringFaceToFaceInterview = false,
    ...restDemographicFields
  } = demographicInformation;

  const isFaceToFaceInterview =
    demographicInformationProvisionType ===
    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

  const { bankruptTypes, ...restFinancesValues } = finances;

  const formattedFinances = {
    bankruptTypes: Object.values(BankruptType).reduce((acc, type) => {
      acc[type] = bankruptTypes?.includes(type);

      return acc;
    }, {}),
    ...restFinancesValues,
  };

  const formattedEthnicity = Object.entries(ethnicity).reduce(
    (acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      switch (true) {
        case key in hispanicOrLatinoEthnicityOptionsMap:
          acc.hispanicOrLatinoOptions = {
            ...acc.hispanicOrLatinoOptions,
            [key]: true,
          };
          break;

        default:
          acc[key] = value;
      }

      return acc;
    },
    {
      hispanicOrLatinoOptions: fillOptionsWithFalse(HispanicOrLatinoEthnicitySubType),
      isEthnicityNotProvidedDuringFaceToFaceInterview: false,
      ...fillOptionsWithFalse(Object.values(EthnicityType)),
    },
  );

  const formattedRace = Object.entries(race).reduce(
    (acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      switch (true) {
        case key in asianRaceOptionsMap && value:
          acc.asianOptions = {
            ...acc.asianOptions,
            [key]: true,
          };
          break;

        case key in nativeHawaiianOrOtherRaceOptionsMap && value:
          acc.nativeHawaiianOrOtherPacificIslanderOptions = {
            ...acc.nativeHawaiianOrOtherPacificIslanderOptions,
            [key]: true,
          };
          break;

        default:
          acc[key] = value;
      }

      return acc;
    },
    {
      asianOptions: fillOptionsWithFalse(AsianRaceSubType),
      nativeHawaiianOrOtherPacificIslanderOptions: fillOptionsWithFalse(
        NativeHawaiianOrOtherRaceSubType,
      ),
      ...fillOptionsWithFalse(RaceType),
      isRaceNotProvidedDuringFaceToFaceInterview: false,
    },
  );

  const defaultDemographicInformationFields = {
    sexCollectedOnTheBasisOfVisualObservationOrSurname: null,
    raceCollectedOnTheBasisOfVisualObservationOrSurname: null,
    ethnicityCollectedOnTheBasisOfVisualObservationOrSurname: null,
  };

  const {
    isServedInMilitary,
    isCurrentlyServing,
    currentlyServingDate,
    isCurrentlyRetired,
    isNonActivatedMemberOfReserveNationalGuard,
    isSurvivingSpouse,
    ...restMilitaryServiceValues
  } = formattedDeclarations.militaryService;

  return {
    ...formattedDeclarations,
    militaryService: {
      isServedInMilitary,
      currentlyServingDate,
      ...replaceEmptyWithFalse({
        isCurrentlyServing,
        isCurrentlyRetired,
        isNonActivatedMemberOfReserveNationalGuard,
        isSurvivingSpouse,
        ...restMilitaryServiceValues,
      }),
    },
    demographicInformation: {
      race: formattedRace,
      ethnicity: formattedEthnicity,
      sex: isFaceToFaceInterview && sex === SEX_NOT_PROVIDED_VALUE ? null : sex,
      rawSex: sex ?? null,
      isSexInfoNotProvidedDuringFaceToFaceInterview,
      demographicInformationProvisionType,
      ...defaultDemographicInformationFields,
      ...restDemographicFields,
    },
    finances: formattedFinances,
  };
};

const getBooleansFromDemographicFields = demographicFields => {
  const {
    ethnicity = {},
    race = {},
    sex,
    demographicInformationProvisionType,
    isSexInfoNotProvidedDuringFaceToFaceInterview,
    ...restDemographicFields
  } = demographicFields;

  const { type: ethnicityType, hispanicOrLatinoOptions, ...restEthnicityFields } = ethnicity;

  const formattedEthnicity = {
    ...getConditionalObject(ethnicityType, {
      [ethnicityType]: true,
    }),
    ...hispanicOrLatinoOptions,
    ...restEthnicityFields,
  };

  const { nativeHawaiianOrOtherPacificIslanderOptions, asianOptions, ...restRaceFields } = race;

  const formattedRace = {
    ...nativeHawaiianOrOtherPacificIslanderOptions,
    ...asianOptions,
    ...restRaceFields,
  };

  return {
    race: formattedRace,
    ethnicity: formattedEthnicity,
    sex,
    demographicInformationProvisionType,
    ...restDemographicFields,
    ...getConditionalObject(
      demographicInformationProvisionType ===
        DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW,
      {
        isSexInfoNotProvidedDuringFaceToFaceInterview,
      },
    ),
  };
};

export const clearFieldsBeforeDispatch = ({ declarations, isBplLoanPurposeType }) => {
  if (!declarations) {
    return null;
  }

  const formattedValues = _.cloneDeep(declarations);

  const { propertyAndLoan, finances, demographicInformation, homeCounseling, militaryService } =
    declarations;

  const isHomeBuyerOther = homeCounseling?.homeBuyerCounseling === HomeBuyerCounseling.OTHER;
  const isCounselingOther = homeCounseling?.counselingFormat === CounselingFormat.OTHER;

  if (demographicInformation?.isRaceNotProvided) {
    hawaiianOptions.forEach(
      fieldName => (formattedValues.demographicInformation[fieldName] = null),
    );
    asianOptions.forEach(fieldName => (formattedValues.demographicInformation[fieldName] = null));
    formattedValues.demographicInformation = clearRaceFields(
      formattedValues.demographicInformation,
    );
  }

  // Keep to direct boolean comparison (it can be null/undefined)
  if (finances?.isLastMonthsBankruptcy === false) {
    formattedValues.finances.bankruptTypes = [];
  }

  // Keep to direct boolean comparison (it can be null/undefined)
  if (propertyAndLoan?.partOfDownPaymentBorrowed === false) {
    formattedValues.propertyAndLoan.partOfDownPaymentBorrowedAmount = null;
  }

  // Keep to direct boolean comparison (it can be null/undefined)
  if (propertyAndLoan?.ownershipInterestPastThreeYears === false) {
    formattedValues.propertyAndLoan.pastThreeYearsPropertyPurpose = null;
    formattedValues.propertyAndLoan.titleHoldingType = null;
  }

  if (isBplLoanPurposeType) {
    formattedValues.propertyAndLoan.intendToOccupyPropertyAsPrimaryResidence = null;
    formattedValues.propertyAndLoan.pastThreeYearsPropertyPurpose = null;
    formattedValues.propertyAndLoan.titleHoldingType = null;
  }

  if (!isHomeBuyerOther && formattedValues.homeCounseling) {
    formattedValues.homeCounseling.counselingOtherDescription = undefined;
  }

  if (!isCounselingOther && formattedValues.homeCounseling) {
    formattedValues.homeCounseling.formatOtherDescription = undefined;
  }

  if (militaryService?.isServedInMilitary) {
    const {
      entitlementCodeFromCoe,
      branchOfService,
      militaryStatus,
      basicEntitlement,
      bonusEntitlement,
      totalEntitlementAmount,
      basicEntitlementManualUpdate,
      bonusEntitlementManualUpdate,
      firstTimeVaUse,
    } = militaryService;

    formattedValues.militaryService = {
      ...militaryService,
      basicEntitlementManualUpdate,
      bonusEntitlementManualUpdate,
      ...replaceEmptyWithNull({
        entitlementCodeFromCoe: entitlementCodeFromCoe?.value,
        branchOfService: branchOfService?.value,
        militaryStatus: militaryStatus?.value,
        basicEntitlement,
        bonusEntitlement,
        totalEntitlementAmount,
        firstTimeVaUse: firstTimeVaUse?.value,
      }),
    };
  }

  return formattedValues;
};

export const getFormattedFormDataByDeclarationType = declarations => {
  if (!declarations) {
    return null;
  }

  return Object.entries(declarations).reduce((formattedDeclarations, [key, value]) => {
    switch (key) {
      case DeclarationType.PROPERTY_AND_LOAN:
        formattedDeclarations[key] = {
          ...value,
          pastThreeYearsPropertyPurpose: value.pastThreeYearsPropertyPurpose?.value || null,
          titleHoldingType: value.titleHoldingType?.value || null,
        };
        break;

      case DeclarationType.MILITARY_SERVICES:
        {
          const { isServedInMilitary, currentlyServingDate, ...restValues } = value;
          formattedDeclarations[key] = {
            isServedInMilitary,
            ...getConditionalObject(isServedInMilitary, () => ({
              currentlyServingDate: currentlyServingDate
                ? dayjs(currentlyServingDate).format(COMMON_REQUEST_DATE_FORMAT)
                : currentlyServingDate,
              ...restValues,
            })),
          };
        }

        break;

      case DeclarationType.DEMOGRAPHIC_INFORMATION:
        formattedDeclarations[key] = getBooleansFromDemographicFields(value);
        break;

      case DeclarationType.COUNSELING:
        formattedDeclarations[key] = {
          ...value,
          homeBuyerCounseling: value.homeBuyerCounseling?.value,
          counselingFormat: value.counselingFormat?.value,
        };
        break;

      case DeclarationType.HOMEOWNERSHIP_EDUCATION_AND_HOUSING_COUNSELING:
        {
          const {
            isBorrowerCompletedHomeownershipEducation,
            isBorrowerCompletedHousingCounseling,
            borrowerHomeownershipEducationFormat,
            borrowerHousingCounselingFormat,
            dateOfCompletionHomeownershipEducation,
            dateOfCompletionHousingCounseling,
            ...restHomeownershipEducationAndHousingCounselingFields
          } = value;

          const { restHomeownershipFields, restHousingCounselingFields, restFields } =
            Object.entries(restHomeownershipEducationAndHousingCounselingFields).reduce(
              (acc, [key, value]) => {
                if (key.startsWith('homeownership')) {
                  acc.restHomeownershipFields[key] = value || null;

                  return acc;
                }

                if (key.startsWith('housingCounseling')) {
                  acc.restHousingCounselingFields[key] = value || null;

                  return acc;
                }

                acc.restValues[key] = value || null;

                return acc;
              },
              {
                restHomeownershipFields: {},
                restHousingCounselingFields: {},
                restFields: {},
              },
            );

          formattedDeclarations[key] = {
            isBorrowerCompletedHomeownershipEducation,
            ...getConditionalObject(isBorrowerCompletedHomeownershipEducation, () => ({
              ...restHomeownershipFields,
              dateOfCompletionHomeownershipEducation: dateOfCompletionHomeownershipEducation
                ? dayjs(dateOfCompletionHomeownershipEducation).format(COMMON_REQUEST_DATE_FORMAT)
                : dateOfCompletionHomeownershipEducation,
              borrowerHomeownershipEducationFormat,
            })),

            isBorrowerCompletedHousingCounseling,
            ...getConditionalObject(isBorrowerCompletedHousingCounseling, () => ({
              ...restHousingCounselingFields,
              dateOfCompletionHousingCounseling: dateOfCompletionHousingCounseling
                ? dayjs(dateOfCompletionHousingCounseling).format(COMMON_REQUEST_DATE_FORMAT)
                : dateOfCompletionHousingCounseling,
              borrowerHousingCounselingFormat,
            })),

            ...restFields,
          };
        }

        break;

      case DeclarationType.LANGUAGE_PREFERENCE:
        formattedDeclarations[key] = {
          ...value,
          languagePreference: value.languagePreference?.value,
        };
        break;
      case DeclarationType.FINANCES:
        {
          const { bankruptTypes = {}, ...restFinancesValues } = value;
          formattedDeclarations[key] = {
            bankruptTypes: Object.entries(bankruptTypes).reduce((acc, [type, value]) => {
              if (value) {
                acc.push(type);
              }

              return acc;
            }, []),
            ...restFinancesValues,
          };
        }

        break;

      default:
        formattedDeclarations[key] = value;
        break;
    }

    return formattedDeclarations;
  }, {});
};

export const customSetInForm = ({
  declarationType,
  customAction,
  entityId,
  setForm,
  setInForm,
  fieldName,
  value,
  blank,
}) => {
  const getSectionForm = () => {
    const ref = {};

    setForm(prev => {
      ref.value = prev[entityId]?.declarations?.[declarationType];

      return prev;
    });

    return ref.value;
  };

  const sectionForm = getSectionForm();

  if (!blank) {
    setInForm(fieldName, value);
  }

  customAction?.(sectionForm);
};
